import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Constants
import QueryParamConstants from 'Shared/constants/QueryParams.constants';

// Types
import { User } from 'Shared/constants/types';
import { QuickStartGuideItemIDs } from 'Dashboard/components/QuickStartGuide/QuickStartGuideItems.constants';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import useUpdateQuickStartGuideFlag from 'Dashboard/components/QuickStartGuide/useUpdateQuickStartGuideFlag';

// Actions
import * as UsersActions from 'Shared/actions/Users.actions';

// Components
import { WpModal, WpModalHeader, WpModalBody, WpModalFooter } from 'Wpds/components/modal';
import { WpButtonGroup, WpButton } from 'Wpds/components/button';
import { WpFormLayout } from 'Wpds/components/form';
import WpTextField from 'Wpds/components/text-field/WpTextField';
import WpUserInput, { WidthType } from 'Wpds/components/user-input/WpUserInput';

//Utils
import { trackEvent } from 'Shared/tracking/tracking';

type Props = {
  user: User;
  isOpen?: boolean;
  toggleIsOpen?: () => void;
};

const EditUserProfileModal = ({ user, isOpen, toggleIsOpen }: Props) => {
  const dispatch = useDispatch();
  const { handleCompleteItem } = useUpdateQuickStartGuideFlag();
  const [isSaving, toggleIsSaving] = useToggle(false);
  const [title, setTitle] = useState(user.title ?? '');
  const [department, setDept] = useState(user.department ?? '');
  const [managerId, setManagerId] = useState(user.managerId ?? '');

  const teamSyncWorkspace = useSelector(state => state.workspaces.find(({ isTeamSync }) => isTeamSync));

  const handleSelectManager = manager => {
    setManagerId(manager.id);
  };

  const handleSubmit = () => {
    toggleIsSaving();

    const data = {
      [QueryParamConstants.Api.DATA_FORMAT]: QueryParamConstants.Api.JSONAPI,
      user: {
        title,
        department,
        manager_id: managerId,
      },
    };
    dispatch(UsersActions.updateUser({ id: user.id, data }))
      .then(() => {
        toggleIsOpen();
        toggleIsSaving();
        trackEvent('Edited User Profile Details');
        handleCompleteItem(QuickStartGuideItemIDs.updateYourProfile);
      })
      .catch(() => {
        toggleIsSaving();
      });
  };

  const handleUnassignManagerSelect = () => {
    setManagerId(null);
  };

  // need to wait for workspaces fetch to get users
  if (!teamSyncWorkspace) return null;

  return (
    <WpModal isOpen={isOpen} size="sm" onToggle={toggleIsOpen}>
      <WpModalHeader>Edit Profile</WpModalHeader>
      <WpModalBody>
        <WpFormLayout>
          <WpTextField id="title" name="title" label="Title" value={title} onChange={setTitle} />
          <WpTextField id="department" name="department" label="Department" value={department} onChange={setDept} />
          <WpUserInput
            id="manager"
            label="Manager"
            users={teamSyncWorkspace.users}
            value={managerId}
            onChange={handleSelectManager}
            optionsWidth={WidthType.target}
            removeUserLabel="Unassign manager"
            handleRemoveUser={handleUnassignManagerSelect}
          />
        </WpFormLayout>
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          <WpButton variant="tertiary" onClick={toggleIsOpen} disabled={isSaving}>
            Cancel
          </WpButton>
          <WpButton onClick={handleSubmit} loading={isSaving}>
            Save
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
};

export default EditUserProfileModal;
